/**
 * Sparkline — lightweight SVG (no chart lib). UX §3.7.
 * Rating trajectory over time, fixed Y-axis 0..5. Accent line, dots at each
 * data point, hairline baseline at 0. Empty state lives in the caller.
 */
import * as React from 'react';
import { cn } from '../../lib/cn';
import { formatShortMonthDay } from '../../lib/date';

export interface SparklinePoint {
  visited_at: string;
  rating: number;
}

export interface SparklineProps extends React.HTMLAttributes<HTMLDivElement> {
  points: SparklinePoint[];
  width?: number;
  height?: number;
  /** Show data-point dots. Default true. */
  showDots?: boolean;
}

export function Sparkline({
  points,
  width = 240,
  height = 40,
  showDots = true,
  className,
  ...rest
}: SparklineProps) {
  if (points.length < 2) {
    return null;
  }

  const padX = 4;
  const padY = 4;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const len = points.length;

  // Sort chronologically (defensive — server returns asc but we don't trust).
  const sorted = [...points].sort(
    (a, b) => new Date(a.visited_at).getTime() - new Date(b.visited_at).getTime(),
  );

  const coords = sorted.map((p, i) => {
    const x = padX + (i / (len - 1)) * innerW;
    const ratingClamped = Math.max(0, Math.min(5, p.rating));
    const y = padY + innerH - (ratingClamped / 5) * innerH;
    return { x, y, p };
  });

  const path = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(' ');

  const baselineY = padY + innerH;

  return (
    <div
      className={cn('inline-block', className)}
      role="img"
      aria-label={`Rating trajectory across ${len} entries`}
      {...rest}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        preserveAspectRatio="none"
      >
        <line
          x1={padX}
          x2={width - padX}
          y1={baselineY}
          y2={baselineY}
          stroke="var(--ink-200)"
          strokeWidth={1}
        />
        <path
          d={path}
          fill="none"
          stroke="var(--ink-900)"
          strokeWidth={1.25}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {showDots
          ? coords.map((c, i) => {
              const point = sorted[i];
              const label = point
                ? `${formatShortMonthDay(point.visited_at)}, rated ${point.rating} dots`
                : '';
              return (
                <circle
                  key={i}
                  cx={c.x}
                  cy={c.y}
                  r={2.5}
                  fill="var(--accent)"
                >
                  <title>{label}</title>
                </circle>
              );
            })
          : null}
      </svg>
    </div>
  );
}
